import { defineStore } from "pinia";
import {
  TrainingImportApi,
  type ImportsResponse,
  type UploadTrainingsCSVRequest,
  TrainingsLmsApi,
  TrainingsApi,
  TrainingsOwnPartnerApi,
  CompanyCatalogApi,
  type CreateOwnPartnerTrainingDto,
  type CreateUpdateTrainingDto,
} from "@simbelapp/order-sdk";
import type { ITrainingsStore } from "~/utils/interfaces/trainings-interface";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { usePagination } from "~/composables/paginations/usePagination";
import { useFilters } from "~/composables/filters/useFilters";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import { useSDKApi } from "~/composables/api/useSDKApi";

export const useTrainingsStore = defineStore("trainingsStore", {
  state: (): ITrainingsStore => {
    return {
      trainingCatalog: null,
      trainings: null,
      trainingDetails: null,
      showAddCatalog: false,
      trainingPageDetails: null,
      currentUrl: null,
    };
  },
  getters: {},
  actions: {
    /**
     * Drops the catalogue training panel's payload, and only that: `$reset()` would also drop
     * `currentUrl` — the return path `catalog/index.vue` branches its modal on — and the cached
     * `trainingCatalog`.
     */
    clearPageDetails() {
      this.trainingPageDetails = null;
    },

    async createUpdateTraining(createUpdateTrainingDto: CreateUpdateTrainingDto) {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingsApi);
      const feedback = useFeedback();

      try {
        const data = await apiInstance.createTraining({ createUpdateTrainingDto });
        return data;
      } catch {
        feedback.error(`erreur`, "small");
      }
    },
    async createOwnTraining(createOwnPartnerTrainingDto: CreateOwnPartnerTrainingDto) {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingsOwnPartnerApi);
      const feedback = useFeedback();

      try {
        const data = await apiInstance.createOwnPartnerTraining({ createOwnPartnerTrainingDto });
        return data;
      } catch {
        feedback.error(`erreur`, "small");
      }
    },
    async fetchTrainingDetails(trainingId?: string) {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, CompanyCatalogApi);

      try {
        const response = await apiInstance.getCompanyCatalog({ trainingId });
        this.trainingCatalog = response?.data?.[0] ?? null;
      } catch {
        this.trainingCatalog = null;
      }
    },
    async getSimbelCatalog() {
      const pagination = usePagination();
      const filters = useFilters();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, CompanyCatalogApi);
      const feedback = useFeedback();

      const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);
      const paginateQuery = new URLSearchParams(paginateOptions);
      const queryString = Object.fromEntries(paginateQuery.entries());
      const filtersQuery = filters.getFiltersForSDK();

      try {
        this.trainings = await apiInstance.getSimbelCatalog({
          ...queryString,
          ...filtersQuery,
          sortingColumn: paginateOptions.sortingColumn,
        });
      } catch {
        feedback.error(`erreur`, "small");
      }
    },
    async fetchOwnPartnerTrainingDetails(trainingId: string) {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingsOwnPartnerApi);

      try {
        this.trainingDetails = await apiInstance.getOwnPartnerTrainingDetails({ trainingId });
      } catch {
        this.trainingDetails = null;
      }
    },
    async fetchTrainingPageDetails(trainingId: string, bookingId?: string) {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingsApi);

      try {
        const response = await apiInstance.getTrainingDetails({ trainingId, bookingId });

        this.trainingPageDetails = response;
      } catch {
        this.trainingPageDetails = null;
      }
    },

    async checkIfTrainingIsLms(trainingId: string) {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingsLmsApi);

      const response = await apiInstance.checkIfTrainingIsLms({ trainingId });
      return response.isLms;
    },

    async updateCreateTrainingAdmin(trainingId: string, adminIds: string[]) {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingsApi);
      const feedback = useFeedback();

      try {
        return await apiInstance.updateCreateTrainingsAdmins({
          trainingId,
          updateTrainingsAdminsDto: { trainingsAdminsIds: adminIds },
        });
      } catch {
        feedback.error(`erreur`, "small");
      }
    },

    async importTrainings(uploadUsersCSV: UploadTrainingsCSVRequest): Promise<ImportsResponse | null> {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingImportApi);
      const response: ImportsResponse | null = await apiInstance
        .uploadTrainingsCSV(uploadUsersCSV)
        .catch(async (error) => {
          const errorResponse = await error.response.json();
          if (errorResponse.message.includes("Parallel import in progress")) {
            feedback.error("Un import est déjà en cours, veuillez attendre avant de relancer", "small");
            return null;
          } else {
            feedback.error("Erreur Interne", "small");
            return null;
          }
        });
      if (response) {
        return response;
      } else {
        return null;
      }
    },
  },
});
